/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Role } from '@touchplan/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, skip, takeUntil } from 'rxjs/operators';
@Component({
	selector: 'multi-role-select',
	templateUrl: './multi-role-select.component.html',
	styleUrls: ['./multi-role-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.Default,
})
export class MultiRoleSelectComponent implements OnInit {
	@Input() public roleList$: Subject<Map<string, Role>>;
	@Input() public styleClass = 'tp-multiselect';
	@Input() public dropdownIcon = 'arrow_drop_down';
	@Input() public panelStyleClass = '';
	@Input() public placeholder = 'All Roles';
	@Input() public sortAlphabetically = true;
	@Input() public selectedRolesIds: string[] = [];
	@Output() public roleChanged: EventEmitter<Role[]> = new EventEmitter();

	public internalField: AbstractControl = new UntypedFormControl('');
	public roleListSubject$: BehaviorSubject<Map<string, Role>> = new BehaviorSubject(new Map());
	private shutdownSubject: Subject<boolean> = new Subject();

	constructor() {}

	public ngOnInit() {
		// skip(1) is used as a workaround to ignore the change triggered by loading the initial value of the mat-extended-select control.
		this.internalField.valueChanges
			.pipe(skip(1), takeUntil(this.shutdownSubject))
			.subscribe((val: Role[]) => {
				this.roleChanged.emit(val);
			});

		this.roleList$.subscribe((newRoles: Map<string, Role>) => {
			const sortedRoles = this.sortAlphabetically
				? new Map(
						[...newRoles.entries()].sort((a, b) => {
							const firstRoleName: string = a[1].view.roleName.toLowerCase();
							const secondRoleName: string = b[1].view.roleName.toLowerCase();
							return firstRoleName.localeCompare(secondRoleName);
						})
				  )
				: newRoles;

			const roleListMap = new Map();
			sortedRoles.forEach((role: Role) => {
				roleListMap.set(role.view.roleName, role);
			});
			this.roleListSubject$.next(roleListMap);
		});

		/**
		 * debounceTime is used as a workaround to ensure the control emits only after the Roles list has fully loaded.
		 * An arbitrary value is used because emitFalse is not functioning correctly in mat-extended-select,
		 * leading to multiple unnecessary emissions and unintended control changes.
		 */
		this.roleList$
			.pipe(debounceTime(350), takeUntil(this.shutdownSubject))
			.subscribe((newRoles: Map<string, Role>) => {
				this.internalField.setValue(
					this.selectedRolesIds.map((roleId) => newRoles.get(roleId)),
					{ emitEvent: false }
				);
			});
	}

	public ngOnDestroy(): void {
		this.shutdownSubject.next(true);
	}
}
